import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';

import { ChartData } from '../../business/chart/common/chart-data';
import { DonutChartSegment } from '../../business/chart/donut/donut-chart-segment';
import { DonutChartFactoryService } from '../../factory/donut-chart-factory.service';

/**
 * This is an unfilled pie chart at its center, which allows you to compare the
 * contribution of different datas with the total.
 * View documentation : https://confluence.si.fr.intraorange/display/ECQ/Composant+Donut+Chart
 */
@Component({
    selector: 'ecm-mat-donut-chart',
    templateUrl: 'material-donut-chart.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [],
})
export class MaterialDonutChartComponent implements OnInit {
    /**
     * @type {ChartData[]}
     */
    @Input() public inputData: ChartData[] = [];
    /**
     * @type {number} Radius of circle
     */
    @Input() public radius = 30;
    /**
     * @type {number} Border width of circle
     */
    @Input() public borderWidth = 5;
    /**
     * @type {string} background s Color
     */
    @Input() public backgroundColor = '#f4f4f4';
    /**
     * @type {boolean} Is animated
     */
    @Input() public animate = false;
    /**
     * @type {string} Circle s width in px/percent/rem
     */
    @Input() public width = '5.625rem';
    /**
     * @type {string} Circle s height in px/percent/rem
     */
    @Input() public height = '5.625rem';
    /**
     * @type {string} Inner label
     */
    @Input() public label: string;
    /**
     * @type {DonutChartSegment[]} List of segments
     */
    public segments: DonutChartSegment[] = [];

    /**
     * Constructor
     * @param {ChangeDetectorRef} changeDetector
     * @param {DonutChartFactoryService} donutChartFactory
     */
    public constructor(
        private changeDetector: ChangeDetectorRef,
        private donutChartFactory: DonutChartFactoryService,
    ) {}

    /**
     * Computes the center of the chart
     * @returns {number} Center of the chart
     */
    public get center(): number {
        return this.radius + this.borderWidth / 2;
    }

    /**
     * The viewBox allows to specify that a given set of graphics stretches to fit a particular container element
     * @returns {string} The viewBox
     */
    public get viewBox(): string {
        return '0 0 ' + (this.center * 2).toString() + ' ' + (this.center * 2).toString();
    }

    /**
     * Adds segments from inputted data
     * Adds a final segment which represents the background
     */
    public ngOnInit(): void {
        // Init the first segment as the background segment
        this.segments.push(this.createBackgroundSegment());

        // Create segments to display
        const sortedSegments: DonutChartSegment[] = this.donutChartFactory.createSortedSegments(
            this.inputData,
            this.radius,
            this.borderWidth,
        );

        // Add segments
        for (const segment of sortedSegments) {
            this.segments.push(segment);
        }

        // If the component only contains 1 segment (the other one is the background)
        // Then it can be considered as a gauge and can be animated
        if (this.segments.length === 2 && this.animate) {
            this.animateSegment(this.segments[1]);
        }
    }

    /**
     * Decreases segment stroke from 100% to its final value
     * @param {DonutChartSegment} segment
     */
    private animateSegment(segment: DonutChartSegment): void {
        let current = 100;
        const segmentInterval = setInterval(() => {
            segment.strokeProgress = current;
            current -= segment.option.percent / 30;
            if (current < segment.option.percent) {
                clearInterval(segmentInterval);
            }
            this.changeDetector.markForCheck();
        }, 32);
    }

    /**
     * Create the background segment
     * @returns {DonutChartSegment}
     */
    private createBackgroundSegment(): DonutChartSegment {
        return this.donutChartFactory.createSegment(this.backgroundColor, 100, this.radius, this.borderWidth);
    }
}
